import i18n from '18n';
import Application from "Application";
import SuccessBuyAssetSharePage from "components/small/successBuyAssetSharePage/SuccessBuyAssetSharePage";
import { LOCAL_STORAGE_KNOWN_KEYS, ONE, TRENDEX_HTTP_STATUS_NOK } from "config/_const";
import { AssetTransactionType, MarketEnum, PaymentMethodsEnum } from "config/_enums";
import useBuyByCredits from "hooks/payment/useBuyByCredits";
import IUseAccount from 'hooks/use-account/IUseAccount';
import useAccount from 'hooks/use-account/useAccount';
import useAssetTransactionSlides from "hooks/use-asset-transaction-slideup/useAssetTransactionSlides";
import useNotification from 'hooks/useNotification';
import useWallet from "hooks/wallet/use-wallet/useWallet";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { dropDataOnGetBestSellingOrder } from "service/direct-buy-from-best-selling-orders/action";
import { managementModal } from "service/modal/action";
import { createSellOrder } from "service/order/actions";
import IAssetTransactionViewModel from "view-model/AssetTransactionViewModel/IAssetTransactionViewModel";
import BuyAssetViewModel from "view-model/BuyAssetViewModel/BuyAssetViewModel";
import SellAssetViewModel from "view-model/SellAssetViewModel/SellAssetViewModel";

const useAssetTransactionConfirmation = (assetTransactionViewModel: IAssetTransactionViewModel, selectedPaymentType: PaymentMethodsEnum) => {
    const { closeAssetTransaction } = useAssetTransactionSlides();
    const { simulateBuy: simulateBuyInWallet, simulateSell: simulateSellInWallet } = useWallet({ considerTeamShares: false });
    const { buyOnPrimaryMarket, directBuyFromBestSellingOrders } = useBuyByCredits({ showErrorsOnSecondaryMarketPaymentError: false });
    const [isTransactionLoading, setTransactionLoading] = useState<boolean>(false);
    const dispatch = useDispatch();
    const { accountViewModel, refreshUser, updateOnboardingStep, refreshUserCredits }: IUseAccount = useAccount();
    const { managementNotification } = useNotification();

    const callBackAfterTxn = () => setTransactionLoading(false);

    const handleSell = () => {
        setTransactionLoading(true);
        const sellAssetViewModel = assetTransactionViewModel as SellAssetViewModel;
        return dispatch(createSellOrder({ ...sellAssetViewModel.CreateOrderPayload, onErrorCallBack: onError, onSuccessCallBack: onSellAssetSuccess }));
    };

    const handleBuy = () => {
        setTransactionLoading(true);
        const buyAssetViewModel = assetTransactionViewModel as BuyAssetViewModel;
        let market = buyAssetViewModel.Market;
        if (Application.getInstance().SkipPrimaryMarket)
            market = MarketEnum.SECONDARY;
        if (market === MarketEnum.PRIMARY) {
            buyOnPrimaryMarket({
                ...buyAssetViewModel.CreditPaymentState,
                transactionSuccessCallback: onSuccess,
                transactionErrorCallback: onError
            },
                buyAssetViewModel.BuyByCreditData,
                callBackAfterTxn,
            );
        } else {
            if (buyAssetViewModel.NumberOfToken.isSmallerThan(ONE)) {
                setTransactionLoading(false);
                managementNotification(true, i18n.t('sm.negative.amount.detected', { amount: buyAssetViewModel.NumberOfToken }), TRENDEX_HTTP_STATUS_NOK);
                return;
            };

            const creditsPaymentState = {
                ...buyAssetViewModel.CreditPaymentState,
                transactionSuccessCallback: onSuccess,
                transactionErrorCallback: onError
            };

            directBuyFromBestSellingOrders(creditsPaymentState,
                buyAssetViewModel.PaymentBestSellingOrderData,
                callBackAfterTxn,
            );
        }
    };

    const handleRefund = () => {
        localStorage.setItem(LOCAL_STORAGE_KNOWN_KEYS.CURRENT_TRANSACTION_ASSET_ID, assetTransactionViewModel.AssetId);
    };

    const onConfirmation = (transactionType: AssetTransactionType) => {
        switch (transactionType) {
            case AssetTransactionType.SELL:
                handleSell();
                break;
            case AssetTransactionType.BUY:
                handleBuy();
                break;
            case AssetTransactionType.REFUND:
                handleRefund();
                break;
        }
    };

    const onSellAssetSuccess = () => {
        dispatch(refreshUserCredits());
        onSuccess();
        if (accountViewModel.IsOnboardingRunning)
            dispatch(updateOnboardingStep());
        return;
    };

    const onSuccess = () => {
        if (assetTransactionViewModel instanceof BuyAssetViewModel) {
            if (!accountViewModel.StripeCustomerId)
                dispatch(refreshUser());
        }
        setTransactionLoading(false);
        let propsModal = {
            display: true,
            disableBackDrop: false,
            showBackArrow: false,
            fullScreen: false,
            type: 'MESSAGE_MODAL',
            showCancelButton: true,
            fullWidth: false,
            propsCustom: {}
        };

        if (assetTransactionViewModel.TransactionType === AssetTransactionType.BUY && assetTransactionViewModel.Asset.enableCustomAssetProfil() && assetTransactionViewModel.Asset.IsAssetBenzema)
            propsModal = {
                ...propsModal,
                fullWidth: true,
                type: "BENZEMA_PACK",
                propsCustom: {
                    totalAmount: assetTransactionViewModel.Amount,
                    totalTokens: assetTransactionViewModel.NumberOfToken,
                    fromPack: assetTransactionViewModel.FromPack
                }
            };
        else {
            propsModal = {
                ...propsModal,
                propsCustom: {
                    customClass: "success-buy-asset-SharePage_customModal",
                    showCancelButton: false,
                    showLinkButton: false,
                    message: (
                        <>
                            <SuccessBuyAssetSharePage
                                asset={assetTransactionViewModel.Asset}
                                tokenQuantity={assetTransactionViewModel.NumberOfToken}
                                totalPrice={assetTransactionViewModel.Amount}
                                totalFeesFormatted={assetTransactionViewModel.TotalFeesFormatted}
                                totalTransactionFormatted={assetTransactionViewModel.TotalTransactionFormatted}
                                transactionType={assetTransactionViewModel.TransactionType}
                                orderExecuted={assetTransactionViewModel.IsImmediateSettlement}
                                userYield={accountViewModel.shouldShowYield()}
                                paymentMethod={selectedPaymentType}
                                amountPaidInVirtualCredit={assetTransactionViewModel.AmountPaidInVirtualCredit}
                                isLiquidityEnabled={assetTransactionViewModel.IsLiquidityPoolApplicable || false}
                            />
                        </>),
                },
            };
        }
        dispatch(managementModal(true, propsModal));
        if (assetTransactionViewModel instanceof BuyAssetViewModel)
            simulateBuyInWallet(assetTransactionViewModel);
        if (assetTransactionViewModel instanceof SellAssetViewModel)
            simulateSellInWallet(assetTransactionViewModel);
        if (Application.getInstance().IsInjectedAsIframe) {
            return;
        }
        closeAssetTransaction();
    };

    const onError = (_transactionData?: any) => {
        setTransactionLoading(false);
        managementNotification(true, _transactionData?.error ?? _transactionData?.intentStatus?.error, TRENDEX_HTTP_STATUS_NOK);
        const buyAssetViewModel = assetTransactionViewModel as BuyAssetViewModel;
        if (buyAssetViewModel.Market === MarketEnum.SECONDARY)
            dispatch(dropDataOnGetBestSellingOrder());
    };

    const hideTransactionError = () => {
        managementNotification(false);
    };

    return { onConfirmation, onError, onSuccess, hideTransactionError, isTransactionLoading };

};

export default useAssetTransactionConfirmation;
